import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {signUpUser, signInUser, forgotPassword, verifyOtp, resetPassword } from "../../api.js";
import { FaHome } from "react-icons/fa";
import { Eye, EyeOff } from "lucide-react";
import loginImage from "../../../assets/login3.png";
import signupImage from "../../../assets/signup1_1.jpg";
import { useTheme } from "../../../context/ThemeContext";

const AuthPage = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const { login, logout, isLoggedIn, redirectAfterLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [resetToken, setResetToken] = useState("");
  const { isDarkTheme } = useTheme();

    // Redirect to /chat if already logged in
    useEffect(() => {
      if (isLoggedIn) {
        navigate("/chat");
      }
    }, [isLoggedIn, navigate]);

  // Handle URL changes & extract token, email from URL
  useEffect(() => {
    const token = searchParams.get("resetToken");
    if (token) {
      setResetToken(token);
      setEmail(searchParams.get("email") || "");
      setShowForgotPassword(true);
      setResetStep(3); // Skip to password reset if token is present
    }
  }, [location.search]);

  useEffect(() => {
    if (!isLoggedIn) {
      if (showForgotPassword) {
        navigate(`/auth/${resetStep === 1 ? "forgot-password" : "reset-password"}`);
      } else {
        navigate("/auth");
      }
    }
  }, [showForgotPassword, resetStep, isLoggedIn, navigate]);

  const handleGoogleSignIn = async (e) => {
    e.preventDefault();
    window.location.href = `/api/google/auth`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Please fill out all fields.");
      setTimeout(() => {
        setError(""); 
      }, 5000);
      return;
    }

    try {
      const response = await signInUser(email, password, rememberMe);
      if (response.message === "Sign-in successful") {
        login(response.user_id, response.access_token, rememberMe);
        navigate(redirectAfterLogin || "/chat");
      } else {
        setError("Invalid email or password.");
        setTimeout(() => {
          setError(""); 
        }, 5000);
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.detail) {
        setError(`Error: ${err.response.data.detail}`);
      } else {
        setError("An error occurred while logging in.");
      }
      setTimeout(() => {
        setError("");
      }, 5000);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (!fullName || !email || !password || !confirmPassword) {
      setError("Please fill out all fields.");
      setTimeout(() => {
        setError(""); 
      }, 5000);
      return;
    }
    
    if (password !== confirmPassword) {
      setError("Passwords do not match!");
      setTimeout(() => {
        setError("");
      }, 5000);
      return;
    }
    if (!agreedToTerms) {
      setError("You must agree to the terms and conditions.");
      setTimeout(() => {
        setError("");
      }, 5000);
      return;
    }
    try {
      const response = await signUpUser(fullName, email, password);
      if (response.message === 'Sign-up successful') {
        toast.success("Account created successfully! Please log in.");
        setIsSignUp(false);
      } else {
        setError("Sign-up failed.");
        setTimeout(() => {
          setError(""); 
        }, 5000);
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.detail) {
        setError(`Error: ${err.response.data.detail}`);
      } else {
        setError("An error occurred while signing up.");
      }
      setTimeout(() => {
        setError(""); 
      }, 5000);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError("Please enter your email address");
      setTimeout(() => {
        setError(""); 
      }, 5000);
      return;
    }
    try {
      const response = await forgotPassword(email);
      if (response.status === 'success') {
        toast.success("Reset instructions sent to your email");
        navigate("/auth/forgot-password");
        setResetStep(2);
      }
      else {
        toast.error("Some error occured while sending OTP!");
      }

    } catch (err) {
      setError(err.response?.data?.detail || "Error sending reset instructions");
      setTimeout(() => {
        setError(""); 
      }, 5000);
    }
  };

  const handleVerifyOTP = async () => {
    try {
      const response = await verifyOtp(email, otp);
      if (response.status === "success") {
        setResetStep(3);
      } else {
        setError("Invalid OTP");
      }
    } catch (error) {
      setError("Invalid or expired OTP");
    }
  };

  const handlePasswordReset = async () => {
    if (!otp && !resetToken) {
      setError("Please provide OTP or use reset link");
      setTimeout(() => {
        setError(""); 
      }, 5000);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    try {
      const response = await resetPassword(resetToken, newPassword, otp, email);
      if (response.status === 'success') {
        toast.success("Password reset successfully!");
        setShowForgotPassword(false);
        setResetStep(1);
        navigate("/auth");
      }
      else {
        toast.error("Some error occured resetting the password!");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Error resetting password");
      setTimeout(() => {
        setError(""); 
      }, 5000);
    }
  };

  const handleLogout = () => {
    logout();
    setEmail("");
    setPassword("");
    setError("");
    navigate("/");
  };

  const handleOtpChange = (index, value) => {
    const newOtp = otp.split('');
    newOtp[index] = value;
    setOtp(newOtp.join(''));

    // Auto focus to next input
    if (value && index < 5) {
      document.getElementById(`otp-input-${index + 1}`).focus();
    }
  };

  const toggleForm = () => {
    setIsSignUp((prev) => !prev);
    setError("");
    setShowForgotPassword(false);
    setResetStep(1);
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${
      isDarkTheme 
        ? 'bg-gradient-to-r from-black/90 to-black/80' 
        : 'bg-gradient-to-r from-white/100 to-white/50'
    }`}>
      <div className={`relative w-full max-w-4xl max-h-[95vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row ${
        isDarkTheme 
          ? 'bg-black/50' 
          : 'bg-white'
      }`}>
        <AnimatePresence mode='wait'>
          {/* Image Section */}
          <motion.div
            className={`hidden md:block w-full md:w-[48%] ${isSignUp ? "order-1" : "order-2"}`}
            key={`${isSignUp ? "signup" : "login"}-${showForgotPassword ? "forgot" : "main"}`}
            initial={{ opacity: 0, x: isSignUp ? 50 : -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isSignUp ? 50 : -50 }}
            transition={{ duration: 0.3 }}
          >
            {showForgotPassword ? (
              <div className="relative h-full w-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center p-8">
                <div className="absolute inset-0 overflow-hidden">
                  {[...Array(6)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute bg-white/10 rounded-full"
                      style={{
                        width: Math.random() * 60 + 20,
                        height: Math.random() * 60 + 20,
                        top: Math.random() * 100,
                        left: Math.random() * 100
                      }}
                      initial={{ y: 0 }}
                      animate={{ y: [0, 50, 0] }}
                      transition={{
                        duration: Math.random() * 5 + 5,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                  ))}
                </div>

                <div className="relative z-10 text-center space-y-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="mx-auto bg-white/20 p-4 rounded-full w-20 h-20 flex items-center justify-center">
                      <svg
                        className="w-12 h-12 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    </div>
                  </motion.div>

                  <motion.h2
                    className="text-4xl font-medium text-white"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    Reset Your Password
                  </motion.h2>

                  <motion.p
                    className="text-white/90 text-lg"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.6 }}
                  >
                    {resetStep === 1
                      ? "Enter your email to receive a verification code"
                      : resetStep === 2
                        ? "Enter the 6-digit code sent to your email"
                        : "Create a new password for your account"}
                  </motion.p>
                </div>
              </div>
            ) : (
              <img
                src={isSignUp ? signupImage : loginImage}
                alt="Auth Visual"
                className="w-full h-full object-cover"
              />
            )}
          </motion.div>

          {/* Form Section */}
          <motion.div
            className={`w-full md:w-[52%] flex flex-col items-center justify-center p-6 space-y-4 overflow-y-auto ${isSignUp ? "order-2" : "order-1"}`}
            key={isSignUp ? "signup-form" : "login-form"}
            initial={{ x: isSignUp ? 50 : -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: isSignUp ? 50 : -50, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="max-w-md mx-auto w-full space-y-4">
              <div className="flex justify-center">
                <button
                  className={`p-2 rounded-full  transition-all duration-300 ${
                    isDarkTheme 
                      ? 'bg-gradient-to-r from-amber-600 to-amber-900 text-white hover:from-amber-600 hover:to-amber-700' 
                      : 'bg-gradient-to-r from-amber-400 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700'
                  }`}
                  onClick={() => navigate("/")}
                >
                  <FaHome size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <h2 className={`text-3xl font-normal text-center ${
                  isDarkTheme 
                    ? 'text-amber-500' 
                    : 'text-amber-600'
                }`}>
                  {showForgotPassword
                    ? resetStep === 1
                      ? "Enter Email Address"
                      : resetStep === 2
                        ? "Enter OTP"
                        : "Enter New Password"
                    : isSignUp
                      ? "Create Account"
                      : "Welcome Back!"}
                </h2>

                {!showForgotPassword ? (
                  <>
                    <p className={`text-center font-medium ${
                      isDarkTheme 
                        ? 'text-gray-300' 
                        : 'text-gray-600'
                    }`}>
                      {isSignUp
                        ? "Join our community and start your journey"
                        : "Please enter your credentials to continue"}
                    </p>

                    <form onSubmit={isSignUp ? handleSignUp : handleLogin} className="space-y-4">
                      <button
                        type="button"
                        onClick={handleGoogleSignIn}
                        className={`w-full flex items-center justify-center gap-3 py-2.5 rounded-xl border transition-all duration-300 shadow-sm ${
                          isDarkTheme 
                            ? 'bg-gray-900 border-gray-800 text-gray-300 hover:bg-gray-800' 
                            : 'bg-white text-gray-700 border-gray-300 hover:border-amber-500'
                        }`}
                      >
                        <img
                          src="https://www.svgrepo.com/show/475656/google-color.svg"
                          className="w-6 h-6"
                          alt="Google logo"
                        />
                        <span className="font-medium">Sign in with Google</span>
                      </button>

                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className={`w-full border-t ${
                            isDarkTheme 
                              ? 'border-gray-800' 
                              : 'border-gray-300'
                          }`}></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className={`px-2 ${
                            isDarkTheme 
                              ? 'text-gray-400' 
                              : 'bg-white text-gray-500'
                          }`}>or continue with email</span>
                        </div>
                      </div>

                      {isSignUp && (
                        <div>
                          <input
                            type="text"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            placeholder="Full Name"
                            className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 ${
                              isDarkTheme 
                                ? 'bg-gray-900 border-gray-800 text-gray-300 focus:ring-amber-600' 
                                : 'border-gray-300 focus:border-amber-500 focus:ring-amber-200'
                            }`}
                          />
                        </div>
                      )}

                      <div>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Email Address"
                          className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 ${
                            isDarkTheme 
                              ? 'bg-gray-900 border-gray-800 text-gray-300 focus:ring-amber-600' 
                              : 'border-gray-300 focus:border-amber-500 focus:ring-amber-200'
                          }`}
                        />
                      </div>

                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Password"
                          className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 pr-12 ${
                            isDarkTheme 
                              ? 'bg-gray-900 border-gray-800 text-gray-300 focus:ring-amber-600' 
                              : 'border-gray-300 focus:border-amber-500 focus:ring-amber-200'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                            isDarkTheme 
                              ? 'text-gray-400 hover:text-amber-400' 
                              : 'text-gray-400 hover:text-amber-600'
                          }`}
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>

                      {isSignUp && (
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm Password"
                            className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 pr-12 ${
                              isDarkTheme 
                                ? 'bg-gray-900 border-gray-800 text-gray-300 focus:ring-amber-600' 
                                : 'border-gray-300 focus:border-amber-500 focus:ring-amber-200'
                            }`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                              isDarkTheme 
                                ? 'text-gray-400 hover:text-amber-400' 
                                : 'text-gray-400 hover:text-amber-600'
                            }`}
                          >
                            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      )}

                      {isSignUp && (
                        <div className="flex items-center pt-2">
                          <label className={`flex items-center gap-2 text-sm ${
                            isDarkTheme 
                              ? 'text-gray-300' 
                              : 'text-gray-600'
                          }`}>
                            <input
                              type="checkbox"
                              checked={agreedToTerms}
                              onChange={(e) => setAgreedToTerms(e.target.checked)}
                              className={`rounded border-gray-300 focus:ring-2 ${
                                isDarkTheme 
                                  ? 'bg-gray-800 text-amber-500 focus:ring-amber-600' 
                                  : 'text-amber-500 focus:ring-amber-500'
                              }`}
                            />
                            <span>I agree to the <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className={`hover:underline ${
                                isDarkTheme 
                                ? 'text-amber-400 hover:text-amber-300' 
                                : 'text-amber-600 hover:text-amber-700'
                            }`}>Terms and Conditions</a></span>
                          </label>
                        </div>
                      )}

                      {!isSignUp && (
                        <div className="flex items-center justify-between pt-2">
                          <label className={`flex items-center gap-2 text-sm ${
                            isDarkTheme 
                              ? 'text-gray-300' 
                              : 'text-gray-600'
                          }`}>
                            <input
                              type="checkbox"
                              checked={rememberMe}
                              onChange={(e) => setRememberMe(e.target.checked)}
                              className={`rounded border-gray-300 focus:ring-2 ${
                                isDarkTheme 
                                  ? 'bg-gray-800 text-amber-500 focus:ring-amber-600' 
                                  : 'text-amber-500 focus:ring-amber-500'
                              }`}
                            />
                            <span>Remember me</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              setShowForgotPassword(true);
                              navigate("/auth/forgot-password");
                            }}
                            className={`text-sm hover:underline ${
                              isDarkTheme 
                                ? 'text-amber-400 hover:text-amber-300' 
                                : 'text-amber-600 hover:text-amber-700'
                            }`}
                          >
                            Forgot Password?
                          </button>
                        </div>
                      )}

                      <button
                        type="submit"
                        className={`w-full py-3 rounded-xl transition-all duration-300 ${
                          isDarkTheme 
                            ? 'bg-gradient-to-r from-amber-600 to-amber-900 text-white hover:from-amber-600 hover:to-amber-700' 
                            : 'bg-gradient-to-r from-amber-400 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700'
                        }`}
                      >
                        {isSignUp ? "Create Account" : "Sign In"}
                      </button>
                    </form>
                  </>
                ) : (
                  /* Password Reset Flow */
                  <div className="space-y-4">
                    {resetStep === 1 ? (
                      <>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your registered email"
                          className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 ${
                            isDarkTheme 
                              ? 'bg-gray-900 border-gray-800 text-gray-300 focus:ring-amber-600' 
                              : 'border-gray-300 focus:border-amber-500 focus:ring-amber-200'
                          }`}
                        />
                        <button
                          onClick={handleForgotPassword}
                          className={`w-full py-3 rounded-xl transition-all duration-300 ${
                            isDarkTheme 
                              ? 'bg-amber-900 text-amber-400 hover:bg-amber-800' 
                              : 'bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700'
                          }`}
                        >
                          Send OTP
                        </button>
                      </>
                    ) : resetStep === 2 ? (
                      <>
                        <div className="flex justify-center gap-3">
                          {[...Array(6)].map((_, index) => (
                            <input
                              key={index}
                              id={`otp-input-${index}`}
                              type="text"
                              maxLength={1}
                              value={otp[index] || ''}
                              onChange={(e) => handleOtpChange(index, e.target.value)}
                              className={`w-12 h-12 text-center text-2xl border-2 rounded-lg focus:outline-none focus:ring-2 ${
                                isDarkTheme 
                                  ? 'bg-gray-900 border-gray-800 text-gray-300 focus:ring-amber-600' 
                                  : 'border-gray-300 focus:border-amber-500 focus:ring-amber-200'
                              }`}
                            />
                          ))}
                        </div>
                        <button
                          onClick={handleVerifyOTP}
                          className={`w-full py-3 rounded-xl mt-4 transition-all duration-300 ${
                            isDarkTheme 
                              ? 'bg-amber-900 text-amber-400 hover:bg-amber-800' 
                              : 'bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700'
                          }`}
                        >
                          Verify OTP
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="New Password"
                            className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 pr-12 ${
                              isDarkTheme 
                                ? 'bg-gray-900 border-gray-800 text-gray-300 focus:ring-amber-600' 
                                : 'border-gray-300 focus:border-amber-500 focus:ring-amber-200'
                            }`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                              isDarkTheme 
                                ? 'text-gray-400 hover:text-amber-400' 
                                : 'text-gray-400 hover:text-amber-600'
                            }`}
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>

                        <div className="relative">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm New Password"
                            className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 pr-12 ${
                              isDarkTheme 
                                ? 'bg-gray-900 border-gray-800 text-gray-300 focus:ring-amber-600' 
                                : 'border-gray-300 focus:border-amber-500 focus:ring-amber-200'
                            }`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                              isDarkTheme 
                                ? 'text-gray-400 hover:text-amber-400' 
                                : 'text-gray-400 hover:text-amber-600'
                            }`}
                          >
                            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>

                        {newPassword && confirmPassword && newPassword !== confirmPassword && (
                          <p className={`text-red-500 text-sm ${
                            isDarkTheme 
                              ? 'text-red-400' 
                              : 'text-red-500'
                          }`}>Passwords do not match</p>
                        )}

                        <button
                          onClick={handlePasswordReset}
                          disabled={newPassword !== confirmPassword}
                          className={`w-full py-3 rounded-xl transition-all duration-300 ${
                            isDarkTheme 
                              ? 'bg-amber-900 text-amber-400 hover:bg-amber-800 disabled:opacity-50 disabled:cursor-not-allowed' 
                              : 'bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed'
                          }`}
                        >
                          Reset Password
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => {
                        setShowForgotPassword(false);
                        setResetStep(1);
                        navigate("/auth");
                      }}
                      className={`w-full text-center transition-colors duration-300 ${
                        isDarkTheme 
                          ? 'text-amber-400 hover:text-amber-300' 
                          : 'text-amber-600 hover:text-amber-700'
                      }`}
                    >
                      Back to Login
                    </button>
                  </div>
                )}

                {error && <div className={`text-center text-sm ${
                  isDarkTheme 
                    ? 'text-red-400' 
                    : 'text-red-500'
                }`}>{error}</div>}

                {!showForgotPassword && (
                  <motion.p
                    className={`text-center cursor-pointer transition-colors duration-300 font-medium underline md:no-underline ${
                      isDarkTheme 
                        ? 'text-gray-300 hover:text-amber-400' 
                        : 'text-gray-600 hover:text-amber-600'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    onClick={toggleForm}
                  >
                    {isSignUp ? (
                      "Already have an account? Login"
                    ) : (
                      "Not registered yet? Create an Account"
                    )}
                  </motion.p>
                )}
              </div>
            </div>
            {isLoggedIn && (
              <button
                onClick={handleLogout}
                className={`w-full py-3 rounded-xl transition-all duration-300 ${
                  isDarkTheme 
                    ? 'bg-red-900 text-red-400 hover:bg-red-800' 
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                Log Out
              </button>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
      <ToastContainer />
    </div>
  );
};

export default AuthPage;